import * as React from "react";
import { ThemeProvider } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import {
  educationRecordRetentionSentence,
  ferpaCoverageSentence,
  schoolOfficialDesignation,
  studentStatementSentence,
} from "constants/educationRecordPolicy";
import theme from "../../theme/theme";

const TermsOfUse: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default, // Use theme background color
          py: 8, // Padding top/bottom
        }}
      >
        <Container maxWidth="md">
          <Grid container spacing={4} justifyContent="center">
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography
                variant="h3"
                component="h1"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                  mb: 4,
                }}
              >
                Terms of Use for VetNebula System
              </Typography>
              <Divider sx={{ mb: 4 }} />
            </Grid>

            {/* Section 1: Introduction and Acceptance */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                1. Introduction and Acceptance of Terms
              </Typography>
              <Typography variant="body1" paragraph>
                Welcome to VetNebula, a system designed to provide coaching,
                operations, and resources for veterinary professionals and
                learners. These Terms of Use defined below ("Terms"), govern
                your access to and use of the VetNebula website, services, and
                applications (collectively, the "Service"). By accessing or
                using the Service, you agree to be bound by these Terms and our
                Privacy Policy. If you do not agree to these Terms, you may not
                use the Service. The Terms of this agreement are also agreed
                upon with the “Organization” you work with, who may have their
                own additional policies and requirements for use. An
                “Organization” is any entity that is working with VetNebula
                contractually or in development, which may include employers,
                educational institutions, or internship providers.
              </Typography>
            </Grid>

            {/* Section 2: Service Description */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                2. Service Description
              </Typography>
              <Typography variant="body1" paragraph>
                VetNebula provides a platform for veterinary professionals to
                access needed web and mobile software services to interact with
                other veterinary professionals, whether they are experienced or
                new to the field. The Service hosts “Interactions” which include
                feedback and requests for actions. These Interactions are
                exchanged via text and structured messages, to or from learners,
                new employees, existing employees, staff, or faculty. The goal
                of Interactions through the Service is to enable the
                Organization to achieve their educational, people operations,
                and staff needs.
              </Typography>
              <Typography variant="body1" paragraph>
                Organizations may directly or through the VetNebula team, upload
                additional educational materials, community forums, and other
                resources aimed at enhancing professional development and
                well-being.
              </Typography>
              <Typography variant="body1" paragraph>
                The Service is intended to supplement the operational work,
                compliance, and staff interactions. It is not a replacement or
                supplier of professional advice, training, or medical judgment.
                VetNebula is not a service to guarantee, warrant, or assure
                accreditation, compliance, or completion of requirements.
              </Typography>
              <Typography variant="body1" paragraph>
                VetNebula is a platform to enable such professional acts but the
                responsibility of user acts and comments is fully on the
                hospital, clinic, educational institution, governing body, or
                Organization that has connected any users to the VetNebula
                platform, systems, or applications.
              </Typography>
            </Grid>

            {/* Section 3: User Accounts and Responsibilities */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                3. User Accounts and Responsibilities
              </Typography>
              <Typography variant="body1" paragraph>
                To access features of the Service, you are required to create an
                account. Doing so you agree to:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 4 }}>
                <li>
                  Provide accurate, current, and complete information during the
                  registration process, as well as adjust information as needed.
                </li>
                <li>
                  Maintain the confidentiality of your account password and be
                  responsible for all activities that occur under your account.
                </li>
                <li>
                  Notify VetNebula immediately of any unauthorized use of your
                  account.
                </li>
                <li>
                  Use the Service only for lawful purposes and in accordance
                  with these Terms.
                </li>
                <li>
                  Not engage in any activity that interferes with or disrupts
                  the Service or the servers and networks connected to the
                  Service.
                </li>
                <li>
                  Not upload, post, or transmit any content whether general or
                  directed at another user, that is unlawful, harmful,
                  threatening, abusive, harassing, defamatory, vulgar, obscene,
                  or otherwise objectionable. This includes comments that are
                  negative or against race, religion, ethnicity, country of
                  origin, gender identity, sexuality, physical conditions,
                  handicaps, or disabilities.
                </li>
                <li>
                  Not upload any unprompted personal information. While we need
                  to collect basic personal information such as your name,
                  email, phone number. We require you not upload additional
                  information such as your date of birth, social security
                  number, credit card information, or any information related to
                  your personal financial assets or access/information related
                  to them.
                </li>
              </Typography>
            </Grid>

            {/* Section 4: Intellectual Property Rights */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                4. Intellectual Property Rights
              </Typography>
              <Typography variant="body1" paragraph>
                All content, trademarks, service marks, trade names, logos, and
                intellectual property displayed on or through the Service are
                the property of VetNebula or its licensors and are protected by
                applicable intellectual property laws. You may not use,
                reproduce, distribute, modify, or create derivative works of any
                content from the Service without explicit written permission
                from VetNebula.
              </Typography>
            </Grid>

            {/* Section 5: Disclaimer of Warranties */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                5. Disclaimer of Warranties
              </Typography>
              <Typography variant="body1" paragraph>
                The service is provided "as is" and "as available" without any
                warranties of any kind, either express or implied, including but
                not limited to, implied warranties of merchantability, fitness
                for a particular purpose, or non-infringement. VetNebula does
                not warrant that the service will be uninterrupted, error-free,
                secure, or that any defects will be corrected.
              </Typography>
            </Grid>

            {/* Section 6: Limitation of Liability */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                6. Limitation of Liability
              </Typography>
              <Typography variant="body1" paragraph>
                In no event shall VetNebula, its affiliates, directors,
                employees, or agents be liable for any indirect, incidental,
                special, consequential, or punitive damages, including without
                limitation, loss of profits, data, use, goodwill, or other
                intangible losses, resulting from (i) your access to or use of
                or inability to access or use the service; (ii) any conduct or
                content of any Organization or third party on the service; (iii)
                any content obtained from the service; and (iv) unauthorized
                access, use or alteration of your transmissions or content,
                whether based on warranty, contract, tort (including negligence)
                or any other legal theory, whether or not we have been informed
                of the possibility of such damage, and even if a remedy set
                forth herein is found to have failed of its essential purpose.
                Moreover, you acknowledge that VetNebula and its staff are not
                responsible in any way for professional misconduct,
                disrespectful, unprofessional, harassment, aggressive or
                otherwise inappropriate exchanges between users or
                Organizations. VetNebula is also not liable for (or consequences
                related to) content uploaded whether by VetNebula affiliates,
                directors, employees, or agents, Organizational members, or
                individual users.
              </Typography>
            </Grid>

            {/* Section 7: Data and Privacy Policy */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                7. Data and Privacy Policy
              </Typography>
              <Typography variant="body1" paragraph>
                VetNebula agrees to keep personal data private and secure,
                subject to any breaches, limitations, or issues that arise with
                our services. These are currently limited to Amazon Web Services
                (AWS), Hasura Inc, Amplitude, Inc, and Github, Inc. Github, Inc.
                will not have any access to personal data and only interacts
                with our code storage, deployment, and solutions. Amplitude
                receives application error reports and page usage events
                (including record identifiers in page addresses) but not feedback
                content. Hasura may record some personal data in accordance with
                their Privacy Policy (https://hasura.io/docs/2.0/policies/privacy/)
                - by agreeing you also agree to their privacy policy, which is
                standard and secure to the best of VetNebula's knowledge.
              </Typography>
              <Typography variant="body1" paragraph>
                Feedback, assessments, and related records about learners are
                education records maintained on behalf of the learner's
                Organization. Records about a learner are visible to that
                learner, to the coaches who wrote them or mentor the learner,
                and to the Organization's administrators, directors, and
                managers. Other learners cannot see them. Learners can download a copy of their records from their
                profile page and can ask their Organization to correct a record
                they believe is inaccurate or misleading from that record's
                page.
              </Typography>
              <Typography variant="body1" paragraph>
                {educationRecordRetentionSentence()} An Organization may ask
                VetNebula in writing to keep records longer. Deletion still
                requires a review by that Organization's administrators,
                directors, or managers.
              </Typography>
              <Typography variant="body1" paragraph>
                {schoolOfficialDesignation}
              </Typography>
              <Typography variant="body1" paragraph>
                {ferpaCoverageSentence} {studentStatementSentence}
              </Typography>
              <Typography variant="body1" paragraph>
                You agree to allow VetNebula to store personal information
                (emails, phone numbers, private conversations) within AWS
                databases or within Hasura.
              </Typography>
              <Typography variant="body1" paragraph>
                We do not collect billing information from any user, instead we
                partner with Organizations to enable their users. You should
                notify VetNebula and your Organization if you are asked for any
                billing information.
              </Typography>
              <Typography variant="body1" paragraph>
                VetNebula agrees to not sell, share, or solicit data collected
                in our applications. The purpose of this data is to allow the
                application to serve the needs of our client partners and will
                never be shared to third parties and other organizations.
              </Typography>
              <Typography variant="body1" paragraph>
                Individuals can request personal data access (which we fully
                access to individuals via our applications) and deletion, which
                generally will be granted. However, deletion events are subject
                to a review of their administrators, directors, or managers.
              </Typography>
              <Typography variant="body1" paragraph>
                Individual users agree that client organization administrators,
                directors, and/or managers shall be allowed to view VetNebula
                interactions to better understand the needs of the client
                organization.
              </Typography>
              <Typography variant="body1" paragraph>
                To be clear, VetNebula will provide parties with requested data,
                but is not responsible for any holding of data or processing
                data related to interactions, liabilities, compliances or
                interpersonal communications; including but not limited to -
                damaging, hateful, or unprofessional language, sharing of
                personal and professional information, and accidental
                occurrences wherewithin.
              </Typography>
              <Typography variant="body1" paragraph>
                By agreeing to this policy, individuals understand that
                VetNebula requires access to all interactions on the platform,
                subject to the statements above.
              </Typography>
            </Grid>

            {/* Section 8: Termination */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                8. Termination
              </Typography>
              <Typography variant="body1" paragraph>
                We may terminate or suspend your access to the Service
                immediately, without prior notice or liability, for any reason
                whatsoever, including without limitation if you breach the
                Terms. Upon termination, your right to use the Service will
                immediately cease.
              </Typography>
            </Grid>

            {/* Section 9: Governing Law */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                9. Governing Law
              </Typography>
              <Typography variant="body1" paragraph>
                These Terms shall be governed and construed in accordance with
                the laws adherent to the countries, states, provinces or local
                areas where your Organization operates.
              </Typography>
            </Grid>

            {/* Section 10: Changes to Terms */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                10. Changes to Terms
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right, at our sole discretion, to modify or
                replace these Terms at any time. If a revision is material, we
                will try to provide at least 30 days' notice prior to any new
                terms taking effect. What constitutes a material change will be
                determined at our sole discretion. By continuing to access or
                use our Service after those revisions become effective, you
                agree to be bound by the revised terms.
              </Typography>
            </Grid>

            {/* Section 11: Contact Information */}
            <Grid size={{ md: 8, xs: 12 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                11. Contact Information
              </Typography>
              <Typography variant="body1" paragraph>
                If you have any questions about these Terms, please contact us
                at contact@vetnebula.com.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default TermsOfUse;
